import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { HardDriveDownload, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Dialog, DialogContent } from '@fas/ui';
import { useAuthStore } from '@/lib/auth-store';
import { formatDateTime } from '@/lib/format';
import { dismissOfflineEvent, loadOfflineQueue, syncOfflineEvents, useOfflineQueue } from '@/lib/offline-queue';
import { useOnlineStatus } from '@/lib/pwa';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Clock events saved on this device (§57, Phase 2D): sends them whenever a connection is
 * available — at start, when it returns and every minute while events wait — and lists those
 * the server did not accept, with its reason.
 */
export function OfflineQueueStatus() {
  const events = useOfflineQueue((s) => s.events);
  const userId = useAuthStore((s) => s.me?.user.id);
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const [reviewing, setReviewing] = useState(false);
  const mine = events.filter((e) => e.userId === userId);
  const pending = mine.filter((e) => e.status === 'PENDING').length;
  const rejected = mine.filter((e) => e.status === 'REJECTED');

  useEffect(() => {
    void loadOfflineQueue();
  }, []);

  useEffect(() => {
    if (!online || pending === 0) return;
    const send = () =>
      syncOfflineEvents()
        .then(async (r) => {
          if (r.accepted > 0) {
            toast.success(`${plural(r.accepted, 'clock event', 'clock events')} recorded offline ${r.accepted === 1 ? 'was' : 'were'} sent for review`);
            await queryClient.invalidateQueries({ queryKey: ['/attendance'] });
          }
          if (r.rejected > 0) toast.error(`${plural(r.rejected, 'record', 'records')} made offline ${r.rejected === 1 ? 'was' : 'were'} not accepted`);
        })
        .catch(() => undefined);
    void send();
    const timer = setInterval(send, 60_000);
    return () => clearInterval(timer);
  }, [online, pending, queryClient]);

  if (pending === 0 && rejected.length === 0) return null;
  return (
    <div className="print:hidden">
      {pending > 0 ? (
        <div role="status" className="flex items-center gap-2 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-950">
          <HardDriveDownload className="size-4 shrink-0" aria-hidden />
          <span>
            {plural(pending, 'clock event is', 'clock events are')} saved on this device {online ? '— sending…' : '— sent automatically when you are back online'}.
          </span>
        </div>
      ) : null}
      {rejected.length > 0 ? (
        <div role="status" className="flex flex-wrap items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-950">
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          <span>{plural(rejected.length, 'record made offline was', 'records made offline were')} not accepted.</span>
          <Button size="sm" variant="outline" onClick={() => setReviewing(true)}>
            Review
          </Button>
        </div>
      ) : null}
      <Dialog open={reviewing && rejected.length > 0} onOpenChange={setReviewing}>
        {rejected.length > 0 ? (
          <DialogContent title="Records made offline that were not accepted" description="If you attended, submit a correction request from the attendance record.">
            <ul className="space-y-3">
              {rejected.map((e) => (
                <li key={e.clientRequestId} className="rounded-md border border-slate-200 p-3 text-sm">
                  <p className="font-medium">
                    {e.kind === 'CLOCK_IN' ? 'Clock-in' : 'Clock-out'} · {e.subject} · {formatDateTime(e.capturedAt)}
                  </p>
                  <p className="mt-1 text-slate-700">{e.message ?? 'The server did not accept this record.'}</p>
                  <Button className="mt-2" size="sm" variant="outline" onClick={() => void dismissOfflineEvent(e.clientRequestId)}>
                    Dismiss
                  </Button>
                </li>
              ))}
            </ul>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
